import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {BatchLinkProps} from 'constants/SampleConstants';
import {LookupConfigDescriptionProps} from 'lookup-config/LookupConfigDescription';
import {getSnapshotFieldOption} from 'registry/field/FieldTableOptionGetters';
import {
  FieldOption,
  getFieldOptionPureTextLabel,
  isFieldOptionHeader,
  isFieldOptionSubmenu,
  LeafFieldOption,
  SchemaField,
} from 'schemas/fields/SchemaFieldConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {OptionData} from 'ui-library/menus/MenuConstants';
import {getMatchingOptionDatas} from 'ui-library/menus/MenuUtil';
import OptionContent from 'ui-library/menus/OptionContent';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {ellipsize} from 'util/NameUtil';

import FieldDefinitionLabel from './FieldDefinitionLabel';

export interface Props {
  dropdownClassName?: string;
  shouldEllipsize?: boolean;
  availableFieldOptions: Array<FieldOption>;
  schemaFieldSelectors: Array<SchemaFieldSelector>;
  batchLinkProps?: BatchLinkProps;
  currentFieldState: SchemaField;
  inEditMode: boolean;
  onSelectFieldOption: (fieldOption: LeafFieldOption) => void;
  ownerUrlId?: string;
  registryId?: string | null;
  lookupConfigDescriptionProps?: LookupConfigDescriptionProps;
  isFieldBeingCreated: boolean;
  disableFieldDefinitionDropdownTooltip?: string | null;
  dataTestAttrs?: DataTestAttrs;
  disableRightSidePadding?: boolean;
}

export function fieldOptionToOptionData(fieldOption: FieldOption): OptionData {
  const base = {
    name: fieldOption.label,
    pureTextName: getFieldOptionPureTextLabel(fieldOption),
    metadata: fieldOption.metadata,
  };
  if (isFieldOptionSubmenu(fieldOption)) {
    return {
      ...base,
      icon: fieldOption.icon,
      submenu: [fieldOption.submenu.map((option) => fieldOptionToOptionData(option))],
    };
  } else if (isFieldOptionHeader(fieldOption)) {
    return {...base, isHeader: fieldOption.isHeader, className: classNames(fieldOption.extraClasses)};
  } else if (fieldOption.fieldDefinition != null) {
    const {extraClasses, ...optionDataValue} = fieldOption;
    return {
      ...base,
      icon: fieldOption.icon,
      value: optionDataValue,
      className: classNames(extraClasses),
      disabled: fieldOption.disabled,
      disabledMessage: fieldOption.disabledMessage,
    };
  }
  throw new Error(
    `Expected either a submenu of field options, or a field option, instead got ${fieldOption.label}`
  );
}

/**
 * Render option content for a field definition option data.
 *
 * Custom behavior: Limit the display text to max. 2 lines, and add a tooltip with the full text if the
 * display text was ellipsized. (Skips this if optionData.name isn't a string, which is currently only true
 * for batch link field definition options).
 */
export function renderFieldDefinitionOptionContent(optionData: OptionData): JSX.Element {
  const displayContent =
    typeof optionData.name === 'string' ? ellipsize(optionData.name, 56) : optionData.name;
  const tooltip =
    optionData.disabledMessage || (displayContent !== optionData.name ? (optionData.name as string) : null);
  return (
    <OptionContent
      icon={optionData.icon}
      iconClassName={optionData.iconClassName}
      hasSubmenu={Boolean(optionData.submenu)}
      tooltipTitle={tooltip}
      dataTestAttrs={{component: 'FieldDefinitionInputOrDisplay', element: 'optionData'}}
      metadata={optionData.metadata}
    >
      {displayContent}
    </OptionContent>
  );
}

export default function FieldDefinitionInputOrDisplay(props: Props): JSX.Element {
  const selectedFieldOptionLabel = (
    <FieldDefinitionLabel
      field={props.currentFieldState}
      schemaFieldSelectors={props.schemaFieldSelectors}
      lookupConfigDescriptionProps={props.lookupConfigDescriptionProps}
      ownerUrlId={props.ownerUrlId ?? null}
      registryId={props.registryId}
      batchLinkProps={props.batchLinkProps}
      inEditMode={props.inEditMode}
    />
  );

  const editsDisabled =
    props.currentFieldState.isParentLink || props.disableFieldDefinitionDropdownTooltip != null;
  return (
    <div className="simpleDivTable-cell">
      <div
        className={props.disableRightSidePadding ? undefined : 'simpleDivTable-cell-contents--last'}
        {...getTestProps(
          props.dataTestAttrs ?? {component: 'FieldDefinitionInputOrDisplay', element: 'root'}
        )}
      >
        {props.inEditMode ? (
          <TooltipWrapper tooltipTitle={props.disableFieldDefinitionDropdownTooltip}>
            {!props.isFieldBeingCreated && props.currentFieldState.isSnapshot ? (
              <Button
                buttonStyle={ButtonStyle.TERTIARY_WHITE}
                size={ButtonSize.LARGE}
                className={css(styles.snapshotButton)}
                text={
                  <div className={css(styles.snapshotButtonText)}>
                    {selectedFieldOptionLabel}
                    <div className={classNames(css(styles.iconWrapper), 'faded')}>
                      <BenchlingIcon icon="edit" />
                    </div>
                  </div>
                }
                onClick={(e) => {
                  props.onSelectFieldOption(getSnapshotFieldOption());
                }}
                disabled={editsDisabled}
              />
            ) : (
              <SelectDropdown
                displayName={selectedFieldOptionLabel}
                menu={
                  <SearchableMenu
                    getMatchingOptionDatas={(query: string, optionDatas: Array<OptionData>) =>
                      getMatchingOptionDatas(query, optionDatas, {
                        includeParentOptionNameInSubOptionName: false,
                      })
                    }
                    optionDatas={props.availableFieldOptions.map((option) => fieldOptionToOptionData(option))}
                    selectedValue={props.currentFieldState}
                    dropLeft={true}
                    onSelect={props.onSelectFieldOption}
                    renderOptionContent={renderFieldDefinitionOptionContent}
                  />
                }
                disabled={editsDisabled}
                buttonClassName={classNames(css(styles.dropdownButton))}
                className={classNames(editsDisabled ? 'disabled--withBorder' : null, props.dropdownClassName)}
                shouldEllipsize={props.shouldEllipsize != null ? props.shouldEllipsize : undefined}
              />
            )}
          </TooltipWrapper>
        ) : (
          selectedFieldOptionLabel
        )}
      </div>
    </div>
  );
}

// exported for test
export const styles = StyleSheet.create({
  subOption: {
    // Link field sub-options have multiple lines of text that need to fit, so give a larger height
    height: '44px',
  },
  snapshotButtonText: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  snapshotButton: {
    height: '100%',
  },
  iconWrapper: {marginLeft: '30px'},
  dropdownButton: {
    minWidth: '75px',
  },
});
